import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import DocumentUploader from "./DocumentUploader";
import DocumentList from "./DocumentList";
import { IDocumentModel } from "./IDocumentModel";
import { fileTypeValidation, isMaxUploadSizeLimitExceeded, validateFileSize } from "./Helper/HelperFunctions";
import { IAttachmentModel } from "../../Core/Domain/Compose/IComposeModel";
import { Loader } from "react-overlay-loader";
import { bytesToSize } from "./Helper/HelperFunctions";
import {
    fetchUploadAttachmentSasLink,
    removeAttachment,
    saveDocuments,
} from "../../actionCreators/composeActionCreators";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "src/store";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import "./style.scss";
import { getFileName } from "src/helper/HelperFunctions";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { CustomModal } from "../Common/Modal/CustomModal";
import { UploadFunctions } from "@sssuite-js-packages/file-utility";
import { fetchFileTypeSettings } from "src/actionCreators/adminSettingsActionCreators";

interface DocumentFormProps {
    uploadDocumentLink?: any;
    onContinueclick?: () => void;
}

const DocumentForm: React.FC<DocumentFormProps> = props => {
    const [documents, setDocuments] = React.useState<IDocumentModel[]>([]);
    const [outlookAttachments, setOutlookAttachments] = React.useState<IDocumentModel[]>([]);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const composeState = useSelector((state: ApplicationState) => state.composeState);
    const dispatch = useDispatch();
    const uploadedCount = documents.filter(d => d.isUploaded).length;
    const documentsRef = React.useRef([]);
    const outlookAttachmentsRef = React.useRef([]);
    documentsRef.current = documents;
    outlookAttachmentsRef.current = outlookAttachments;
    const fileUploadUtilities = new UploadFunctions();

    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );

    React.useEffect(() => {
        dispatch(fetchUserSettings());
        dispatch(fetchFileTypeSettings());
    }, []);

    React.useEffect(() => {
        if (documents.length > 0) {
            onUploadFile(documents);
        }
    }, [documentsRef.current.length]);

    React.useEffect(() => {
        getAttachmentsFromMailboxItem();
        Office.context.mailbox.item.addHandlerAsync(Office.EventType.AttachmentsChanged, e => {
            if (e.attachmentStatus === Office.MailboxEnums.AttachmentStatus.Added) onAttachmentChangeEvent(e);
        });
        if (composeState?.documents?.length > 0) {
            setDocuments(composeState.documents);
        }

        return () => Office.context.mailbox.item.removeHandlerAsync(Office.EventType.AttachmentsChanged);
    }, []);

    const getAttachmentsFromMailboxItem = () => {
        Office.context.mailbox.item.getAttachmentsAsync(result => {
            if (result?.value?.length > 0) {
                for (let i = 0; i < result.value.length; i++) {
                    if (!result.value[i].isInline) {
                        const attachment: any = result.value[i];
                        Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, result => {
                            validateAndAddAttachment(attachment.id, result, attachment.name, attachment.contentType);
                        });
                    }
                }
            }
        });
    };

    const onAttachmentChangeEvent = event => {
        if (!event.attachmentDetails.isInline) {
            switch (event.attachmentStatus) {
                case Office.MailboxEnums.AttachmentStatus.Added: {
                    const attachmentDetails = event.attachmentDetails;
                    Office.context.mailbox.item.getAttachmentContentAsync(attachmentDetails.id, result =>
                        validateAndAddAttachment(
                            attachmentDetails.id,
                            result,
                            attachmentDetails.name,
                            attachmentDetails.contentType
                        )
                    );
                    break;
                }
                default:
                    break;
            }
        }
    };

    const validateAndAddAttachment = (attachmentId, result, name, attachmentType) => {
        switch (result.value.format) {
            case Office.MailboxEnums.AttachmentContentFormat.Base64: {
                let file;
                const bstr = atob(result.value.content);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);

                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                try {
                    file = new File([u8arr], name, { type: attachmentType });
                } catch (e) {
                    const blob = new Blob([u8arr], { type: attachmentType });
                    blob["name"] = name;
                    blob["lastModifiedDate"] = new Date();
                    file = Object.assign(blob) as File;
                }
                handleOnDropFiles([file]);
                Office.context.mailbox.item.removeAttachmentAsync(attachmentId);
                break;
            }
            default:
                AppNotifier.Error(ToasterMessages.ERROR.FILE_FORMAT_NOT_SUPPORTED);
                break;
        }
    };

    const handleOnDropFiles = (files: File[], callback?: any) => {
        let totalSize = 0;
        documents.forEach(document => {
            totalSize += document.size;
        });
        Array.from(files).forEach((item: File) => {
            totalSize += item.size;
        });
        const newFiles: IDocumentModel[] = [];
        let showFileTypeErrorMsg = false;
        const allowedFileTypes = messageOptionsSettings?.fileTypeSettings?.allowedFileTypes ?? [];
        for (let i = 0; i < files.length; i++) {
            if (fileTypeValidation(files[i].name, allowedFileTypes, "")) {
                if (!validateFileSize(files[i])) {
                    break;
                }
                if (!isMaxUploadSizeLimitExceeded(totalSize)) {
                    break;
                }
                const guid = uuidv4();
                newFiles.push({
                    file: files[i],
                    progressBar: 0,
                    guid,
                    isUploaded: false,
                    name: files[i].name,
                    size: files[i].size,
                    displayName: getFileName(files[i].name)
                });
                logger.trackEvent({
                    name: `${appInsightsData.Compose.pageTitle} - ${appInsightsData.Compose.Operation.AttachmentAdded}`,
                    properties: {
                        page: appInsightsData.Compose.pageTitle,
                        guid,
                        file: files[i],
                    },
                });
            }
            else {
                showFileTypeErrorMsg = true;
            }
        }

        if (showFileTypeErrorMsg) {
            AppNotifier.Error(ToasterMessages.ERROR.UNSUPPORTED_FILE_TYPE.replace("{0}", allowedFileTypes.join(", ")));
        }

        const updatedFiles = [...documentsRef.current].concat(newFiles);
        setDocuments(updatedFiles);
        if (callback && typeof callback === "function") {
            callback();
        }
    };

    const handleOnRemoveAttachment = (guid: string) => {
        dispatch(
            removeAttachment(guid, (data: any) => {
                let updatedFiles: IDocumentModel[] = [...documents];
                updatedFiles = updatedFiles.filter(x => x.guid !== guid);
                setDocuments(updatedFiles);
            })
        );
    };

    const onUploadFile = (documents: IDocumentModel[]) => {
        for (let i = 0; i < documents.length; i++) {
            const fileName = getFileName(documents[i].file.name);
            if (
                !documents[i].isUploaded &&
                documents[i].progressBar === 0 &&
                outlookAttachmentsRef.current.find(x => x.guid === documents[i].guid) === undefined
            ) {
                setOutlookAttachments([...outlookAttachments, documents[i]]);
                dispatch(
                    fetchUploadAttachmentSasLink(documents[i].guid, (data: any) => {
                        fileUploadUtilities.uploadFile(documents[i].file, data, fileName, uploadProgressCallback, uploadCompleteCallback);
                    })
                );
            }
        }
    };

    const uploadProgressCallback = (percent: number, fileToUpload: any) => {
        const updatedState = documentsRef.current.map(item => {
            if (item.guid === fileToUpload.fileGUID) {
                item.uploadProgress = (percent == 100 ? percent - 10 : percent) / 100;
            }
            return item;
        });
        setDocuments(updatedState);
    };

    const uploadCompleteCallback = (fileToUpload: any, errorMessage?: string) => {
        if (fileToUpload.uploadFailed) {
            setDocuments(documentsRef.current.filter(file => file.guid != fileToUpload.fileGUID));
            if (errorMessage) {
                AppNotifier.Warning(errorMessage);
            }
            return;
        }
        const updatedState = documentsRef.current.map(item => {
            if (item.guid === fileToUpload.fileGUID && !fileToUpload.uploadFailed) {
                item.progressBar = 1;
                item.isUploaded = true;
            }
            return item;
        });
        setDocuments(updatedState);
    };

    const onContinueClick = () => {
        if (documents.length > 0 && uploadedCount === documents.length) {
            let attachments: IAttachmentModel[] = [];
            attachments = documents.map(item => {
                return {
                    FileName: item.file.name,
                    PhysicalPath: item.guid,
                    Size: item.file.size,
                    IsComplete: item.isUploaded ? 1 : 0,
                    UploadProgress: item.isUploaded ? 1 : 0,
                    IsBlob: 1,
                    ConvertedSize: bytesToSize(item.file.size),
                };
            });
            props.onContinueclick();
            dispatch(saveDocuments(documents));
            logger.trackEvent({
                name: `${appInsightsData.Compose.pageTitle} - ${appInsightsData.Compose.Operation.ContinueButtonClick}`,
                properties: {
                    uploadedDocumentIds: documents.map(document => document.guid),
                },
            });
        } else if (documents.length === 0) {
            setShowConfirmModal(true);
        } else {
            AppNotifier.Warning(ToasterMessages.WARNING.ATTACHMENT_UPLOAD_INCOMPLETE);
        }
    };

    const handleConfirmModalClose = () => {
        setShowConfirmModal(false);
    };

    const handleConfirmModalContinue = () => {
        setShowConfirmModal(false);
        props.onContinueclick();
    };

    return (
        <>
            <DocumentUploader handleOnDropFiles={handleOnDropFiles} />
            <br />
            <DocumentList documents={documents} onRemoveAttachment={handleOnRemoveAttachment} />
            <div className="footer-btns">
                <button
                    className="button-primary-blue-2 continue-button"
                    onClick={onContinueClick}
                >
                    Continue
                </button>
            </div>
            <Loader classNamePrefix="sm" text="" loading={composeState?.isRemovingAttachment} />
            <CustomModal
                show={showConfirmModal}
                onHide={handleConfirmModalClose}
                title="Warning"
                onSubmit={handleConfirmModalContinue}
                cancelButtonName="Cancel"
                confirmButtonName="Continue"
                isConfirmModal={true}
            >
                <p>You have not attached any files to this message. Are you sure you want to continue without any attachments?</p>
            </CustomModal>
        </>
    );
};
export default DocumentForm;
